/**
 * useShellSessionEntry - the ways into a session, and out of it.
 *
 * Create (as GM), join (by code) and rejoin (a stored session) share one
 * path: an unreachable backend makes the SDK retry for ever, so the wait is
 * bounded; on success the seat is set, saved for a later rejoin and the offer
 * cleared; on failure the reason is said in a toast. A player comes in as an
 * identity the server can check (anonymous) with no login screen; the GM
 * signs in with a password or a Google popup. Extracted from App; every message is unchanged.
 */
import { useState, useCallback } from 'react';
import { backend } from '../backend/app';
import { withTimeout } from '../utils/withTimeout';
import { saveSession, clearStoredSession } from '../utils/sessionStorage';
import { diag, errorSummary } from '../diagnostics/log';

const SESSION_TIMEOUT_MS = 10_000;
const UNREACHABLE_MSG = 'Could not reach the server - check your connection and try again.';

/** The name this seat goes by: typed, else the account's, else Unknown. */
export function seatName(displayName, user) {
  return displayName.trim() || (user && user.displayName) || 'Unknown';
}

/**
 * Enter a session: bounded wait, then seat, save and clear the offer - or a
 * toast. `seat(r)` turns the backend's answer into the session (null to
 * ignore an empty answer); `onFail` runs after the toast.
 */
function enterSession({ request, seat, name, verb, failed, prefix, onFail }, d) {
  return withTimeout(request, SESSION_TIMEOUT_MS, UNREACHABLE_MSG)
    .then((r) => {
      const sess = seat(r);
      if (!sess) return;
      d.setSession(sess);
      saveSession({ ...sess, displayName: name, storedAt: Date.now() });
      d.setPendingRejoin(null);
      diag.info('session', `${verb} ${sess.id}`);
    })
    .catch((err) => {
      diag.error('session', failed, errorSummary(err));
      d.pushToast(prefix + err.message, { type: 'error', duration: 8000 });
      onFail?.();
    });
}

/** A busy flag, and a runner that holds it for one promise. */
function useBusy() {
  const [busy, setBusy] = useState(false);
  const run = useCallback((start) => {
    setBusy(true);
    start().finally(() => setBusy(false));
  }, []);
  return [busy, run];
}

/** Create a session as its GM. */
function createSession(d, name) {
  return enterSession(
    {
      request: backend.sessions.create(name),
      seat: (r) => r && { id: r.sessionId, code: r.joinCode, isGm: true, playerId: r.playerId },
      name,
      verb: 'created',
      failed: 'create failed',
      prefix: 'Failed to create session: ',
    },
    d,
  );
}

/** Join a session by its code. */
function joinSession(d, name, code) {
  return enterSession(
    {
      request: backend.sessions.join(code, name),
      seat: (r) => ({ id: r.sessionId, code, isGm: false, playerId: r.playerId }),
      name,
      verb: 'joined',
      failed: 'join failed',
      prefix: 'Failed to join: ',
    },
    d,
  );
}

/** Create and join, sharing the session-busy flag. */
function useCreateAndJoin(d, name) {
  const [sessionBusy, runSession] = useBusy();
  const handleCreate = () => {
    if (!sessionBusy) runSession(() => createSession(d, name));
  };
  const handleJoin = () => {
    if (!d.joinCodeInput || sessionBusy) return;
    const code = d.joinCodeInput.toUpperCase();
    runSession(() => joinSession(d, name, code));
  };
  return { sessionBusy, handleCreate, handleJoin };
}

/** Rejoin the offered session; a refusal forgets it. */
function useRejoin(d, name) {
  const [rejoining, runRejoin] = useBusy();
  const handleRejoin = () => {
    if (!d.pendingRejoin) return;
    const forget = () => {
      clearStoredSession();
      d.setPendingRejoin(null);
    };
    runRejoin(() =>
      enterSession(
        {
          request: backend.sessions.rejoin(d.pendingRejoin.id, name),
          seat: (r) => ({ id: r.sessionId, code: r.joinCode, isGm: r.isGm, playerId: r.playerId }),
          name,
          verb: 'rejoined',
          failed: 'rejoin failed',
          prefix: 'Failed to rejoin: ',
          onFail: forget,
        },
        d,
      ),
    );
  };
  return { rejoining, handleRejoin };
}

/** The player's way in and the GM's, sharing the auth-busy flag. */
function useSignIns(pushToast) {
  const [authBusy, runAuth] = useBusy();
  const handlePlay = () => {
    if (authBusy) return;
    runAuth(() =>
      backend.auth.signIn('anonymous').catch((err) => {
        diag.error('auth', 'guest sign-in failed', errorSummary(err));
        pushToast('Could not start: ' + err.message, { type: 'error', duration: 8000 });
      }),
    );
  };
  // The popup is the one way in a person can walk away from. The adapter
  // resolves a window closed on purpose and throws a sentence for the rest
  // (blocked, offline), so this says it as the password path does - and
  // the busy flag keeps a second click from cancelling the first popup.
  const handleGoogleSignIn = () => {
    if (authBusy) return;
    runAuth(() =>
      backend.auth.signIn('google').catch((err) => {
        diag.error('auth', 'Google sign-in failed', errorSummary(err));
        pushToast('Sign in failed: ' + err.message, { type: 'error', duration: 8000 });
      }),
    );
  };
  const handlePasswordSignIn = (e, { identity, password, clearPassword }) => {
    e.preventDefault();
    if (authBusy) return;
    runAuth(() =>
      backend.auth
        .signIn('password', { identity: identity.trim(), password })
        .then(() => clearPassword())
        .catch((err) => {
          diag.error('auth', 'GM sign-in failed', errorSummary(err));
          pushToast('Sign in failed: ' + err.message, { type: 'error', duration: 8000 });
        }),
    );
  };
  return { authBusy, handlePlay, handleGoogleSignIn, handlePasswordSignIn };
}

/**
 * @param {object} d - user, displayName, joinCodeInput, pendingRejoin,
 *   setSession, setPendingRejoin, pushToast.
 */
export default function useShellSessionEntry(d) {
  const name = seatName(d.displayName, d.user);
  const handleDismissRejoin = () => {
    clearStoredSession();
    d.setPendingRejoin(null);
  };
  const { setSession } = d;
  const handleLeaveSession = useCallback(() => {
    clearStoredSession();
    setSession({ id: null, code: null, isGm: false, playerId: null });
  }, [setSession]);
  return {
    ...useCreateAndJoin(d, name),
    ...useRejoin(d, name),
    ...useSignIns(d.pushToast),
    handleDismissRejoin,
    handleLeaveSession,
  };
}
